import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

const Feedback = () => {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState(""); // State to hold feedback text
  const [selectedEmoji, setSelectedEmoji] = useState(null); // State to track selected emoji
  const [message, setMessage] = useState(""); // State to display feedback message
  const [isSubmitting, setIsSubmitting] = useState(false); // State to handle submission status
  const formRef = useRef(null); // Ref to track the feedback form
  const [notification, setNotification] = useState(""); // State to handle notification messages

  const toggleFeedbackForm = () => {
    setShowFeedbackForm(!showFeedbackForm);
    setMessage(""); // Reset message when toggling
  };

  const closeFeedbackForm = () => {
    setShowFeedbackForm(false); // Close feedback form on button click
    setFeedbackText(""); // Reset feedback text
    setSelectedEmoji(null); // Reset selected emoji
    setMessage(""); // Reset message
  };

  // Close form when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setShowFeedbackForm(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to handle emoji click
  const handleEmojiClick = (emoji, text) => {
    setSelectedEmoji(emoji); // Set selected emoji
    setFeedbackText(text); // Update feedback text with corresponding phrase
  };

  // Function to handle feedback submission
  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      setMessage("Please provide some feedback.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/feedbacks",
        {
          emoji: selectedEmoji || "No Emoji Selected", // Default value if no emoji is selected
          feedback: feedbackText.trim(),
        }
      );

      if (response.status === 201 || response.status === 200) {
        setMessage("Thank you for your feedback!");
        setNotification("Feedback submitted successfully!"); // Set success notification
        closeFeedbackForm();
      } else {
        setMessage("Failed to submit feedback. Please try again.");
        setNotification("Something went wrong! Try again."); // Set error notification
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setMessage("An error occurred while submitting your feedback.");
      setNotification("Error occurred! Please try again."); // Set error notification
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setNotification(""), 5000); // Clear notification after 5 seconds
    }
  };

  return (
    <>
      {/* Floating Feedback Button */}
      {!showFeedbackForm && (
        <div className="feedback-fab-container">
          <button className="feedback-fab" onClick={toggleFeedbackForm}>
            Feedback
          </button>
        </div>
      )}

      {/* Feedback Form */}
      {showFeedbackForm && (
        <div className={`feedback-card slide-in`} ref={formRef}>
          <button className="close-button" onClick={closeFeedbackForm}>
            ✖️
          </button>
          <h5 className="feedback-title">Rate Your Experience:</h5>
          <div className="emoji-options">
            <span
              role="img"
              aria-label="excellent"
              className={`emoji ${selectedEmoji === "😊" ? "selected" : ""}`}
              onClick={() => handleEmojiClick("😊", "Excellent")}
            >
              😊
            </span>
            <span
              role="img"
              aria-label="good"
              className={`emoji ${selectedEmoji === "😐" ? "selected" : ""}`}
              onClick={() => handleEmojiClick("😐", "Good")}
            >
              😐
            </span>
            <span
              role="img"
              aria-label="not good"
              className={`emoji ${selectedEmoji === "😟" ? "selected" : ""}`}
              onClick={() => handleEmojiClick("😟", "Need Improvements")}
            >
              😟
            </span>
          </div>
          <div className="feedback-type">
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Leave your comments here..."
              rows="4"
              className="form-control"
            ></textarea>
          </div>
          <button
            className="btn-submit-feedback"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </button>
          {message && <p className="feedback-message">{message}</p>}
          <p className="mt-2">
            Sorry, we can't respond to individual comments. If you need
            assistance, please{" "}
            <Link to="mailto:support@droneplanet.in">Contact Us</Link>.
          </p>
        </div>
      )}

      {/* Notification (Toast-style) */}
      {notification && (
        <div className="feedback-notification">
          <p>{notification}</p>
        </div>
      )}
    </>
  );
};

export default Feedback;
